import { GeckoLocale } from 'types/measurement/geckoLocale'

export type GeckoFormattingVariant = 'geometric' | 'narrow' | 'long' | undefined
export enum DataType {
  Continuous,
  Discrete,
  Categorical,
}
export const InvalidCategoricalValue = 'Invalid Value'

type FormatNumberProps = {
  locale: GeckoLocale
  num: number
  variant: GeckoFormattingVariant
  unit?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

type FormatRangeProps = Omit<FormatNumberProps, 'num'> & {
  start: number
  end: number
}

type NumberFormatterProps = Omit<FormatNumberProps, 'unit'>

type NumberFormatter = (props: NumberFormatterProps) => string

type RangeFormatterProps = Omit<FormatRangeProps, 'unit'>

type RangeFormatter = (props: RangeFormatterProps) => string

const all = new Map<string, GeckoMeasurementUnit>()

/**
 * An abstract representation of a measurement unit like "feet" or "years".
 * Note that this includes Gecko-specific measurement units like "tubes" (for
 * waterwalls). These instances allow standardization of string formatting
 * across applications.
 */
export class GeckoMeasurementUnit {
  //----------------------------------------------------------------------------

  public static readonly None = new GeckoMeasurementUnit(
    '',
    'None',
    {
      formatNumber: ({
        num,
        locale,
        minimumFractionDigits = 0,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant: undefined,
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  public static readonly Percent = new GeckoMeasurementUnit(
    'percent',
    '%',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits = 0,
        maximumFractionDigits = 1,
      }) => {
        return (
          new Intl.NumberFormat(locale, {
            minimumFractionDigits,
            maximumFractionDigits,
            signDisplay: num !== 0 ? 'auto' : 'never',
            unitDisplay: variant === 'geometric' ? 'narrow' : variant,
          }).format(num) + '%'
        )
      },
    },
    DataType.Continuous
  )

  public static readonly Ratio = new GeckoMeasurementUnit(
    'ratio',
    'Ratio',
    {
      formatNumber: ({
        num,
        locale,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant: undefined,
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  //----------------------------------------------------------------------------

  public static readonly Inches = new GeckoMeasurementUnit(
    'in',
    'in',
    {
      /**
       * NOTE: The `geometric` variant overrides the `minimumFractionDigits` and `maximumFractionDigits` options
       */
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        variant === 'geometric'
          ? _formatInchesAsFeetAndInches(locale, num)
          : _formatNumber({
              locale,
              num,
              variant,
              unit: 'inch',
              minimumFractionDigits,
              maximumFractionDigits,
            }),
      /**
       * NOTE: The `geometric` variant overrides the `minimumFractionDigits` and `maximumFractionDigits` options
       */
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        variant === 'geometric'
          ? _formatInchesAsFeetAndInches(locale, start) +
            ' – ' +
            _formatInchesAsFeetAndInches(locale, end)
          : _formatRange({
              locale,
              start,
              end,
              variant,
              unit: 'inch',
              minimumFractionDigits,
              maximumFractionDigits,
            }),
    },
    DataType.Continuous
  )
  public static readonly Feet = new GeckoMeasurementUnit(
    'ft',
    'ft',
    {
      /**
       * NOTE: The `geometric` variant overrides the `minimumFractionDigits` and `maximumFractionDigits` options
       */
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        variant === 'geometric'
          ? _formatInchesAsFeetAndInches(locale, num * 12)
          : _formatNumber({
              locale,
              num,
              variant,
              unit: 'foot',
              minimumFractionDigits,
              maximumFractionDigits,
            }),
      /**
       * NOTE: The `geometric` variant overrides the `minimumFractionDigits` and `maximumFractionDigits` options
       */
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        variant === 'geometric'
          ? _formatInchesAsFeetAndInches(locale, start * 12) +
            ' – ' +
            _formatInchesAsFeetAndInches(locale, end * 12)
          : _formatRange({
              locale,
              start,
              end,
              variant,
              unit: 'foot',
              minimumFractionDigits,
              maximumFractionDigits,
            }),
    },
    DataType.Continuous
  )

  public static readonly SquareFeet = new GeckoMeasurementUnit(
    'sqft',
    'ft²',
    {
      /**
       * NOTE: The `geometric` variant overrides the `minimumFractionDigits` and `maximumFractionDigits` options
       */
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' ft²',
    },
    DataType.Continuous
  )

  public static readonly Millimeters = new GeckoMeasurementUnit(
    'mm',
    'mm',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'millimeter',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )
  public static readonly Thou = new GeckoMeasurementUnit(
    'thou',
    'thou',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' thou',
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatRange({
          start,
          end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' thou',
    },
    DataType.Continuous
  )
  public static readonly Mil = new GeckoMeasurementUnit(
    'mil',
    'mil',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' mil',
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatRange({
          start,
          end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' mil',
    },
    DataType.Continuous
  )
  public static readonly Meters = new GeckoMeasurementUnit(
    'm',
    'm',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'meter',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  public static readonly SquareMeters = new GeckoMeasurementUnit(
    'm2',
    'm²',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ' m²',
    },
    DataType.Continuous
  )

  public static readonly Tubes = new GeckoMeasurementUnit(
    'tubes',
    'tubes',
    {
      formatNumber: ({ num, locale }) => _formatTubeNumber(locale, num),
      formatRange: ({ start, end, locale }) => {
        // Tubes are odd in that a range from tube 1 to tube 2... is just tube 1
        if (Math.abs(end - start) <= 1.0)
          return _formatTubeNumber(locale, start)

        const roundedStart = Math.round(start)
        const roundedEnd = Math.round(end)

        const adjustedStart = start > end ? start - 1.0 : start
        const adjustedEnd = start > end ? end : end - 1.0

        // Again, strangely how tubes work
        return (
          _formatTubeNumber(locale, adjustedStart) +
          ' – ' +
          new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
            signDisplay:
              roundedStart < 0 || roundedEnd < 0 ? 'exceptZero' : 'never',
            unitDisplay: 'narrow',
          }).format(adjustedEnd)
        )
      },
    },
    DataType.Discrete
  )

  // These are a naval unit - each frame is a structural rib of the hull.
  // This means the actual size of each frame depends on the ship's construction.
  // In order to use frames, the asset model must have a 'frame_size' property.
  public static readonly Frames = new GeckoMeasurementUnit(
    'frames',
    'frames',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        'Frame ' +
        GeckoMeasurementUnit.None.formatNumber({
          num,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        'Frame ' +
        GeckoMeasurementUnit.None.formatRange({
          start,
          end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Discrete
  )

  public static readonly Degrees = new GeckoMeasurementUnit(
    'degrees',
    '°',
    {
      formatNumber: ({
        num,
        locale,
        minimumFractionDigits,
        maximumFractionDigits = 0,
      }) =>
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(num) + '°',
      formatRange: ({
        start,
        end,
        locale,
        minimumFractionDigits,
        maximumFractionDigits = 0,
      }) =>
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(start) +
        ' – ' +
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(end) +
        '°',
    },
    DataType.Continuous
  )

  public static readonly Radians = new GeckoMeasurementUnit(
    'radians',
    'rad',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits = 2,
      }) =>
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(num) + (variant === 'long' ? ' radians' : ' rad'),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits = 2,
      }) =>
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(start) +
        ' – ' +
        new Intl.NumberFormat(locale, {
          minimumFractionDigits,
          maximumFractionDigits,
          signDisplay: 'auto',
          unitDisplay: 'narrow',
        }).format(end) +
        (variant === 'long' ? ' radians' : ' rad'),
    },
    DataType.Continuous
  )

  //----------------------------------------------------------------------------

  public static readonly Nanovolts = new GeckoMeasurementUnit(
    'nanovolts',
    'nV',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        (variant === 'long' ? (num === 1 ? ' nanovolt' : ' nanovolts') : ' nV'),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatRange({
          start,
          end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + (variant === 'long' ? ' nanovolts' : ' nV'),
    },
    DataType.Continuous
  )

  //----------------------------------------------------------------------------

  public static readonly Years = new GeckoMeasurementUnit(
    'years',
    'years',
    {
      formatNumber: ({
        num,
        locale,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant: 'narrow',
          minimumFractionDigits,
          maximumFractionDigits,
        }) + (Math.abs(num) === 1 ? ` year` : ` years`),
      formatRange: ({
        start,
        end,
        locale,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatRange({
          locale,
          start,
          end,
          variant: 'narrow',
          minimumFractionDigits,
          maximumFractionDigits,
        }) + ` years`,
    },
    DataType.Continuous
  )
  public static readonly Seconds = new GeckoMeasurementUnit(
    'sec',
    'sec',
    {
      formatNumber: ({ num }) => {
        const hours = Math.floor(num / 3600)
        const mins = Math.floor((num - hours * 3600) / 60)
        const sec = Math.floor(num - hours * 3600 - mins * 60)
        return `${hours}h${mins}m${sec}s`
      },
    },
    DataType.Continuous
  )
  public static readonly InchesPerYear = new GeckoMeasurementUnit(
    'in-per-year',
    'in/yr',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits = 4,
        maximumFractionDigits = 4,
      }) =>
        variant === 'long'
          ? _formatNumber({
              locale,
              num,
              variant,
              unit: 'inch',
              minimumFractionDigits,
              maximumFractionDigits,
            }) + ' per year'
          : GeckoMeasurementUnit.Inches.formatNumber({
              num,
              locale,
              variant: undefined,
              minimumFractionDigits,
              maximumFractionDigits,
            }) + '/yr',
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        variant === 'long'
          ? GeckoMeasurementUnit.Inches.formatRange({
              start,
              end,
              locale,
              variant: 'long',
              minimumFractionDigits,
              maximumFractionDigits,
            }) + ' per year'
          : GeckoMeasurementUnit.Inches.formatRange({
              start,
              end,
              locale,
              variant: undefined,
              minimumFractionDigits,
              maximumFractionDigits,
            }) + '/yr',
    },
    DataType.Continuous
  )
  public static readonly MillimetersPerYear = new GeckoMeasurementUnit(
    'mm-per-year',
    'mm/yr',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.Millimeters.formatNumber({
          num,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + (variant === 'long' ? ' per year ' : '/yr'),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.Millimeters.formatRange({
          start,
          end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + (variant === 'long' ? ' per year ' : '/yr'),
    },
    DataType.Continuous
  )
  public static readonly FeetPerSecond = new GeckoMeasurementUnit(
    'ft-per-sec',
    'ft/s',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'foot-per-second',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' – ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          unit: 'foot-per-second',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )
  public static readonly MetersPerSecond = new GeckoMeasurementUnit(
    'm-per-sec',
    'm/s',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'meter-per-second',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' – ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          unit: 'meter-per-second',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  //----------------------------------------------------------------------------
  public static readonly PoundsForcePerSqInch = new GeckoMeasurementUnit(
    'psi',
    'psi',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'psi',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' - ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          unit: 'psi',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  public static readonly Megapascals = new GeckoMeasurementUnit(
    'MPa',
    'MPa',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          unit: 'MPa',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' - ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          unit: 'MPa',
          minimumFractionDigits,
          maximumFractionDigits,
        }),
    },
    DataType.Continuous
  )

  //----------------------------------------------------------------------------
  public static readonly Fahrenheit = new GeckoMeasurementUnit(
    'fahrenheit',
    '°F',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + '°F',
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' - ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        '°F',
    },
    DataType.Continuous
  )

  public static readonly Celsius = new GeckoMeasurementUnit(
    'celsius',
    '°C',
    {
      formatNumber: ({
        num,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        _formatNumber({
          locale,
          num,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) + '°C',
      formatRange: ({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) =>
        GeckoMeasurementUnit.None.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        ' - ' +
        _formatNumber({
          locale,
          num: end,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        }) +
        '°C',
    },
    DataType.Continuous
  )
  //---------------------------------------------------------------------------

  public static readonly RelationToNominal = new GeckoMeasurementUnit(
    'nom-rel',
    'Relation To Nominal',
    {
      formatNumber: ({ num }) => {
        const displayString = {
          1: 'High Wastage Readings',
          2: 'Unknown Readings',
          3: 'Readings Near Nominal',
        }[num]
        if (!displayString) {
          console.warn(
            `Invalid value processed for a categorical view.  Value = ${num}, valid values are: 
              1 (validated reading with >=15% wastage), 2 (invalidated reading), or 3 (validated reading with <15% wastage)`
          )
          return InvalidCategoricalValue
        }
        return displayString
      },
      formatRange: ({ start, end, ...props }): string =>
        `${this.RelationToNominal.formatNumber({ num: start, ...props })} and ${this.RelationToNominal.formatNumber(
          { num: end, ...props }
        )}`,
    },
    DataType.Categorical,
    [1, 3]
  )

  public static readonly EmatThicknessCondition = new GeckoMeasurementUnit(
    'emat-thickness-condition',
    'EMAT Thickness Condition',
    {
      formatNumber: ({ num }) => {
        const displayString = {
          0: 'No Wastage Data Available',
          1: 'SN/V - Condition not verifiable',
          2: 'S1 - No discernable metal loss',
          3: 'S2 - <5% wastage for steel',
          4: 'S3 - 5-20% wastage for steel',
          5: 'S4 - 20-25% wastage for steel',
          6: 'S5 - >25% wastage for steel',
        }[num]
        if (!displayString) {
          console.warn(
            `Invalid value processed for EMAT thickness condition view. Value = ${num}, valid values are: 0-5`
          )
          return InvalidCategoricalValue
        }
        return displayString
      },
      formatRange: ({ start, end, ...props }): string =>
        `${this.EmatThicknessCondition.formatNumber({ num: start, ...props })} to ${this.EmatThicknessCondition.formatNumber(
          { num: end, ...props }
        )}`,
    },
    DataType.Categorical,
    [0, 6]
  )

  public static readonly EmatCustomCategory = new GeckoMeasurementUnit(
    'emat-custom-category',
    'EMAT Custom Category',
    {
      formatNumber: ({ num }) => {
        const displayString = {
          0: 'No Wastage Data Available',
          1: 'SN/V - Condition not verifiable',
          2: 'S1 - No discernable metal loss',
          3: 'S2 - Minimal wastage (0-5%)',
          4: 'S3 - Moderate wastage (5-20%)',
          5: 'S4 - Significant wastage (20-25%)',
          6: 'S5 - Severe wastage (>25%)',
        }[num]
        if (!displayString) {
          console.warn(
            `Invalid value processed for EMAT custom category view. Value = ${num}, valid values are: 0-6`
          )
          return InvalidCategoricalValue
        }
        return displayString
      },
      formatRange: ({ start, end, ...props }): string =>
        `${this.EmatCustomCategory.formatNumber({ num: start, ...props })} to ${this.EmatCustomCategory.formatNumber(
          { num: end, ...props }
        )}`,
    },
    DataType.Categorical,
    [0, 6]
  )

  //----------------------------------------------------------------------------

  /**
   * A unique string identifier for this measurement unit. This must line up
   * with the convert library or a Gecko-defined term like "tubes" to work
   */
  public readonly id: string

  /** A user-facing name for this measurement unit */
  public readonly name: string

  /** A function which creates a user-facing string for given value */
  public readonly formatNumber: NumberFormatter

  /** A function which creates a user-facing string for given a start and end value */
  public readonly formatRange: RangeFormatter

  /** The range of acceptable values for this MeasurementUnit, inclusive */
  public readonly acceptableValueRange: [number, number]

  /** The type of data this measurement unit represents */
  public readonly dataType: DataType

  private constructor(
    id: string,
    name: string,
    functions: {
      formatNumber: NumberFormatter
      formatRange?: RangeFormatter
    },
    dataType: DataType,
    acceptableValueRange?: [number, number]
  ) {
    this.id = id
    this.name = name
    this.formatNumber = functions.formatNumber
    this.formatRange =
      functions.formatRange ??
      (({
        start,
        end,
        locale,
        variant,
        minimumFractionDigits,
        maximumFractionDigits,
      }) => {
        const startString = this.formatNumber({
          num: start,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        })
        const endString = this.formatNumber({
          num: end,
          locale,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        })
        return `${startString} – ${endString}`
      })
    if (acceptableValueRange) {
      if (
        dataType === DataType.Categorical &&
        (!Number.isFinite(acceptableValueRange[0]) ||
          !Number.isFinite(acceptableValueRange[1]))
      ) {
        throw new Error('Categorical units must have a finite value range')
      }
      this.acceptableValueRange = acceptableValueRange
    } else if (dataType === DataType.Categorical) {
      throw new Error('Categorical units must have a value range')
    } else {
      this.acceptableValueRange = [
        Number.NEGATIVE_INFINITY,
        Number.POSITIVE_INFINITY,
      ]
    }
    this.dataType = dataType
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * Returns the unique measurement unit instance for a given ID if it exists
   * @param id The unique ID of this measurement unit
   */
  public static GetByID(
    id: string | undefined
  ): GeckoMeasurementUnit | undefined {
    return !id ? GeckoMeasurementUnit.None : all.get(id)
  }

  /**
   * Returns the unique measurement unit instance for a given ID, or creates a
   * unique measurement unit instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string): GeckoMeasurementUnit {
    return (
      GeckoMeasurementUnit.GetByID(id) ??
      new GeckoMeasurementUnit(
        id,
        id,
        {
          formatNumber: ({ num, locale }) => num.toLocaleString(locale),
        },
        DataType.Continuous
      )
    )
  }
}

//------------------------------------------------------------------------------

export const _formatNumber = ({
  locale,
  num,
  variant,
  unit,
  minimumFractionDigits,
  maximumFractionDigits = 3,
}: FormatNumberProps) => {
  // If the maximumFractionDigits is less than the minimumFractionDigits, a RangeError is thrown
  const resolvedMinimumFractionDigits =
    minimumFractionDigits ??
    Math.min(variant === 'geometric' ? 0 : 3, maximumFractionDigits)

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: resolvedMinimumFractionDigits,
    maximumFractionDigits,
    signDisplay: num !== 0 ? 'auto' : 'never',
    style: unit ? 'unit' : undefined,
    unit: unit,
    unitDisplay:
      variant === 'geometric'
        ? unit === 'meter'
          ? undefined
          : 'narrow'
        : variant,
  }).format(num)
}

export const _formatRange = ({
  start,
  end,
  locale,
  variant,
  unit,
  minimumFractionDigits,
  maximumFractionDigits,
}: FormatRangeProps) => {
  return (
    new Intl.NumberFormat(locale, {
      minimumFractionDigits:
        minimumFractionDigits ?? (variant === 'geometric' ? 0 : 3),
      maximumFractionDigits:
        maximumFractionDigits ?? (variant === 'geometric' ? 2 : 3),
      signDisplay: start < 0 || end < 0 ? 'exceptZero' : 'never',
      style: unit ? 'unit' : undefined,
      unit: unit,
      unitDisplay: variant === 'geometric' ? 'narrow' : variant,
    }).format(start) +
    ' - ' +
    new Intl.NumberFormat(locale, {
      minimumFractionDigits:
        minimumFractionDigits ?? (variant === 'geometric' ? 0 : 3),
      maximumFractionDigits:
        maximumFractionDigits ?? (variant === 'geometric' ? 2 : 3),
      signDisplay: start < 0 || end < 0 ? 'exceptZero' : 'never',
      style: unit ? 'unit' : undefined,
      unit: unit,
      unitDisplay: variant === 'geometric' ? 'narrow' : variant,
    }).format(end)
  )
}

export const _formatInchesAsFeetAndInches = (
  locale: GeckoLocale,
  num: number
) => {
  // Round this first to avoid 315.999999999 from being formatted as 315' 12"
  const rounded = Math.round(num)
  const feet = rounded < 0 ? Math.ceil(rounded / 12) : Math.floor(rounded / 12)
  const inches = rounded % 12
  const formattedFeet = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'unit',
    unit: 'foot',
    signDisplay: feet > 0 ? 'never' : 'exceptZero',
    unitDisplay: 'narrow',
  }).format(feet)
  if (Math.abs(inches) < 0.001) return formattedFeet

  const formattedInches = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    style: 'unit',
    unit: 'inch',
    signDisplay: feet === 0 && inches <= 0 ? 'exceptZero' : 'never',
    unitDisplay: 'narrow',
  }).format(inches)
  if (feet === 0) return formattedInches

  return formattedFeet + ' ' + formattedInches
}

export const _formatTubeNumber = (
  locale: GeckoLocale,
  num: number,
  options?: Partial<Intl.NumberFormatOptions>
) => {
  const roundedNum = Math.round(num)
  return (
    '#' +
    new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      signDisplay: roundedNum >= 0 ? 'never' : 'auto',
      ...options,
    }).format(roundedNum)
  )
}
